"use client";
/* eslint-disable react-refresh/only-export-components -- useSidebar is the public sidebar context hook. */
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { PanelLeftIcon, Menu } from "lucide-react"

import { useIsMobile } from "../hooks/use-mobile"
import { cn } from "../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Separator } from "./separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./sheet"
import { Skeleton } from "./skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip"

const SIDEBAR_COOKIE_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "var(--ds-sidebar-width)"
const SIDEBAR_WIDTH_MOBILE = "var(--ds-sidebar-width-mobile)"
const SIDEBAR_WIDTH_ICON = "var(--ds-sidebar-width-icon)"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

const SidebarContext = React.createContext(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}) {
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = React.useState(false)

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(() => {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${SIDEBAR_COOKIE_NAME}=([^;]*)`))
      if (match && typeof match[1] !== 'undefined') {
        return match[1] === 'true'
      }
    } catch {
      // Cookie access is unavailable during server rendering.
    }
    return defaultOpen
  })
  const open = openProp ?? _open
  const setOpen = React.useCallback((value) => {
    const openState = typeof value === "function" ? value(open) : value
    if (setOpenProp) {
      setOpenProp(openState)
    } else {
      _setOpen(openState)
    }

    // This sets the cookie to keep the sidebar state.
    document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
  }, [setOpenProp, open])

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile])

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed"

  const contextValue = React.useMemo(() => ({
    state,
    open,
    setOpen,
    isMobile,
    openMobile,
    setOpenMobile,
    toggleSidebar,
  }), [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar])

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style
            }
          }
          className={cn(
            "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
            className
          )}
          {...props}>
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()
  const { t } = useTranslation("common")

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col",
          className
        )}
        {...props}>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE
            }
          }
          side={side}>
          <SheetHeader className="sr-only">
            <SheetTitle>{t("sidebar.title", { defaultValue: "Sidebar" })}</SheetTitle>
            <SheetDescription>{t("sidebar.description", { defaultValue: "Displays the mobile sidebar." })}</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="group peer text-sidebar-foreground hidden lg:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar">
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative w-(--sidebar-width) bg-transparent transition-[width] duration-[var(--ds-motion-duration-standard)] ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+var(--ds-sidebar-floating-width-offset))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
        )} />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed inset-y-0 z-[var(--ds-layer-shell)] hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-[var(--ds-motion-duration-standard)] ease-linear lg:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-[var(--ds-space-2)] group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+var(--ds-sidebar-floating-width-offset))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className
        )}
        {...props}>
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-[var(--ds-radius-control)] group-data-[variant=floating]:border group-data-[variant=floating]:shadow-[var(--shadow-surface)]">
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}) {
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation("common")
  const toggleLabel = t("actions.toggleSidebar", { defaultValue: "Toggle sidebar" })

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      type="button"
      className={cn("size-[var(--ds-density-control-height-sm)]", className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}>
      <Menu aria-hidden />
      <span className="sr-only">{toggleLabel}</span>
    </Button>
  );
}

function SidebarRail({
  className,
  ...props
}) {
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation("common")

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      type="button"
      aria-label={t("actions.toggleSidebar", { defaultValue: "Toggle sidebar" })}
      tabIndex={0}
      onClick={toggleSidebar}
      className={cn(
        "hover:after:bg-sidebar-border absolute inset-y-0 z-[var(--ds-layer-shell)] hidden w-[var(--ds-sidebar-rail-width)] -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-2 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-px sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-1",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-1",
        className
      )}
      {...props} />
  );
}

function SidebarInset({
  className,
  ...props
}) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "bg-background relative flex min-w-0 flex-1 flex-col",
        "lg:peer-data-[variant=inset]:m-[var(--ds-space-2)] lg:peer-data-[variant=inset]:ml-0 lg:peer-data-[variant=inset]:rounded-[var(--ds-radius-surface)] lg:peer-data-[variant=inset]:shadow-[var(--shadow-surface)] lg:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-[var(--ds-space-2)] lg:peer-data-[state=collapsed]:overflow-y-hidden lg:peer-data-[state=collapsed]:h-svh",
        className
      )}
      {...props} />
  );
}

function SidebarInput({
  className,
  ...props
}) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn("h-[var(--ds-density-control-height-sm)] w-full bg-background shadow-none", className)}
      {...props} />
  );
}

function SidebarHeader({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn("flex flex-col gap-[var(--ds-space-2)] p-[var(--ds-space-2)]", className)}
      {...props} />
  );
}

function SidebarFooter({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-[var(--ds-space-2)] p-[var(--ds-space-2)]", className)}
      {...props} />
  );
}

function SidebarSeparator({
  className,
  ...props
}) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("mx-[var(--ds-space-2)] w-auto bg-sidebar-border", className)}
      {...props} />
  );
}

function SidebarContent({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-[var(--ds-space-2)] overflow-auto",
        className
      )}
      {...props} />
  );
}

function SidebarGroup({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col py-[var(--ds-space-2)] pr-0 pl-0 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0", className)}
      {...props} />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot : "div"

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "text-sidebar-foreground/70 ring-sidebar-ring flex h-[var(--ds-density-control-height-sm)] shrink-0 items-center rounded-[var(--ds-radius-control-compact)] px-[var(--ds-space-2)] text-xs font-medium outline-hidden transition-[margin,opacity] duration-[var(--ds-motion-duration-standard)] ease-linear focus-visible:ring-[var(--ds-focus-ring-width)] [&>svg]:size-[var(--ds-icon-size-sm)] [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props} />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute right-[var(--ds-space-3)] top-[var(--ds-sidebar-action-offset)] flex aspect-square w-[var(--ds-icon-size-md)] items-center justify-center rounded-[var(--ds-radius-control-compact)] p-0 outline-hidden transition-transform focus-visible:ring-[var(--ds-focus-ring-width)] [&>svg]:size-[var(--ds-icon-size-sm)] [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props} />
  );
}

function SidebarGroupContent({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props} />
  );
}

function SidebarMenu({
  className,
  ...props
}) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-[var(--ds-space-1)] group-data-[collapsible=icon]:items-center", className)}
      {...props} />
  );
}

function SidebarMenuItem({
  className,
  ...props
}) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center", className)}
      {...props} />
  );
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-[var(--ds-space-1)] rounded-[var(--ds-radius-control-compact)] py-[var(--ds-space-2)] pl-[var(--ds-space-2)] pr-[var(--ds-space-1)] text-left text-xs font-medium text-sidebar-foreground outline-hidden ring-sidebar-ring transition-[width,height,padding] duration-[var(--ds-motion-duration-standard)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-[var(--ds-focus-ring-width)] active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-semibold data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:mx-auto! group-data-[collapsible=icon]:size-[var(--ds-density-control-height-sm)]! group-data-[collapsible=icon]:justify-center! group-data-[collapsible=icon]:gap-0! group-data-[collapsible=icon]:p-[var(--ds-space-2)]! [&>span:last-child]:break-words [&>span:last-child]:whitespace-normal [&>span:last-child]:leading-tight [&>svg]:size-[var(--ds-icon-size-sm)] [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[var(--ds-sidebar-outline-shadow)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[var(--ds-sidebar-outline-shadow-hover)]",
      },
      size: {
        default: "min-h-[var(--ds-density-control-height-sm)] text-xs",
        sm: "min-h-[var(--ds-space-7)] text-xs",
        lg: "min-h-[var(--ds-space-12)] text-xs group-data-[collapsible=icon]:p-0!"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  noWrap = false,
  className,
  ...props
}) {
  const Comp = asChild ? Slot : "button"
  const { isMobile, state } = useSidebar()

  const tooltipText =
    typeof tooltip === "string"
      ? tooltip
      : tooltip?.children ?? null

  const sharedProps = {
    "data-slot": "sidebar-menu-button",
    "data-sidebar": "menu-button",
    "data-size": size,
    "data-active": isActive,
    className: cn(
      sidebarMenuButtonVariants({ variant, size }),
      noWrap && "[&>span:last-child]:whitespace-nowrap truncate",
      className
    ),
    ...props,
  }

  // React 19: Tooltip + Slot/asChild causes composeRefs update loops — use native title.
  if (asChild && tooltipText) {
    return (
      <Comp
        {...sharedProps}
        title={state === "collapsed" && !isMobile ? tooltipText : undefined}
      />
    )
  }

  const button = <Comp {...sharedProps} />

  if (!tooltip) {
    return button
  }

  const tooltipProps = typeof tooltip === "string" ? { children: tooltip } : tooltip

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltipProps} />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute right-[var(--ds-space-1)] top-[var(--ds-sidebar-action-offset-sm)] flex aspect-square w-[var(--ds-icon-size-md)] items-center justify-center rounded-[var(--ds-radius-control-compact)] p-0 outline-hidden transition-transform focus-visible:ring-[var(--ds-focus-ring-width)] [&>svg]:size-[var(--ds-icon-size-sm)] [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        "peer-data-[size=sm]/menu-button:top-[var(--ds-space-1)]",
        "peer-data-[size=default]/menu-button:top-[var(--ds-sidebar-action-offset-sm)]",
        "peer-data-[size=lg]/menu-button:top-[var(--ds-sidebar-action-offset-lg)]",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
        "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 lg:opacity-0",
        className
      )}
      {...props} />
  );
}

function SidebarMenuBadge({
  className,
  ...props
}) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "text-sidebar-foreground pointer-events-none absolute right-[var(--ds-space-1)] flex size-[var(--ds-icon-size-md)] min-w-[var(--ds-icon-size-md)] items-center justify-center rounded-[var(--ds-radius-control-compact)] px-[var(--ds-space-1)] text-xs font-medium tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-[var(--ds-space-1)]",
        "peer-data-[size=default]/menu-button:top-[var(--ds-sidebar-action-offset-sm)]",
        "peer-data-[size=lg]/menu-button:top-[var(--ds-sidebar-action-offset-lg)]",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props} />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, [])

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn("flex h-[var(--ds-density-control-height-sm)] items-center gap-[var(--ds-space-1)] rounded-[var(--ds-radius-control-compact)] px-[var(--ds-space-2)]", className)}
      {...props}>
      {showIcon && (
        <Skeleton className="size-[var(--ds-icon-size-sm)] rounded-[var(--ds-radius-control-compact)]" data-sidebar="menu-skeleton-icon" />
      )}
      <Skeleton
        className="h-[var(--ds-icon-size-sm)] max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width
          }
        } />
    </div>
  );
}

function SidebarMenuSub({
  className,
  ...props
}) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "border-sidebar-border ml-[var(--ds-sidebar-sub-indent)] mr-0 flex min-w-0 translate-x-[var(--ds-sidebar-sub-translate)] flex-col gap-[var(--ds-space-1)] border-l py-[var(--ds-sidebar-sub-border-padding-block)] pl-[var(--ds-sidebar-sub-padding-inline)] pr-0",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props} />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("group/menu-sub-item relative", className)}
      {...props} />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}) {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-auto w-full min-w-0 -translate-x-[var(--ds-sidebar-sub-translate)] items-start gap-[var(--ds-space-1)] overflow-hidden rounded-[var(--ds-radius-control-compact)] py-[var(--ds-sidebar-sub-padding-block)] pl-[var(--ds-space-2)] pr-[var(--ds-space-1)] outline-hidden focus-visible:ring-[var(--ds-focus-ring-width)] disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:break-words [&>span:last-child]:whitespace-normal [&>span:last-child]:leading-tight [&>svg]:size-[var(--ds-icon-size-sm)] [&>svg]:shrink-0",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-xs",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props} />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
